import React from "react";

import CheckIcon from "@mui/icons-material/Check";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { ButtonBase, Stack, Typography } from "@mui/material";
import { SelectDateRange } from "components/forms/Select-date-range/SelectDateRange";
import { useDealActions, useDealQueriesSelect } from "store";

import { dateRangeForStorage } from "./pipeline-utils";
import { usePipelineConfigPersist } from "./use-pipeline-config-persist";

// Segmented "Operational | Diff View" control (Figma node 2701:66230). The active half is filled primary
// with a check; the inactive half sits on the page background. Matches the two-view pattern from the data
// extractor. Diff View turns on `show_stage_diff` in the pipeline request (in/out counters + stage_diff).
const segmentSx = (active: boolean, side: "left" | "right") =>
  ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    px: "8px",
    py: "4px",
    minHeight: "24px",
    fontSize: "12px",
    lineHeight: "16px",
    whiteSpace: "nowrap",
    bgcolor: active ? "#071f35" : "#f6f8fc",
    color: active ? "#ffffff" : "#071f35",
    borderTopLeftRadius: side === "left" ? "32px" : 0,
    borderBottomLeftRadius: side === "left" ? "32px" : 0,
    borderTopRightRadius: side === "right" ? "32px" : 0,
    borderBottomRightRadius: side === "right" ? "32px" : 0,
    transition: "background-color 0.15s linear"
  }) as const;

export const PipelineViewToggle: React.FC = () => {
  const { updateQueries } = useDealActions();
  const { pipelineDiffView, pipelineDateRange } = useDealQueriesSelect();
  const persist = usePipelineConfigPersist();

  const select = (diff: boolean) => {
    if (diff === pipelineDiffView) return;
    updateQueries({ pipelineDiffView: diff });
    persist({ pipelineDiffView: diff });
  };

  return (
    <Stack direction="row" alignItems="center" gap="8px">
      <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#071f35", whiteSpace: "nowrap" }}>
        View:
      </Typography>
      <Stack direction="row" alignItems="center" gap="2px">
        <ButtonBase onClick={() => select(false)} sx={segmentSx(!pipelineDiffView, "left")}>
          {!pipelineDiffView && <CheckIcon sx={{ fontSize: "16px" }} />}
          Operational
        </ButtonBase>
        <ButtonBase onClick={() => select(true)} sx={segmentSx(pipelineDiffView, "right")}>
          {pipelineDiffView && <CheckIcon sx={{ fontSize: "16px" }} />}
          Diff View
        </ButtonBase>
      </Stack>

      {/* Diff View filters by deal created_at — reuse the reports date-range selector (presets + custom). */}
      {pipelineDiffView && (
        <SelectDateRange
          value={pipelineDateRange}
          onChange={(value) => {
            // Relative presets are stored as just their key (dates regenerated at request time); absolute
            // ranges keep their picked dates. The select still shows the right option (it reads `value.value`).
            const stored = dateRangeForStorage(value);
            updateQueries({ pipelineDateRange: stored });
            persist({ pipelineDateRange: stored });
          }}
          placeholder="Select"
          hideDateRangeExtras
          formControlProps={{ sx: { minWidth: "200px" } }}
          selectProps={{
            startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
          }}
        />
      )}
    </Stack>
  );
};
